import React, { FC } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { BackButton } from 'components/BackButton/BackButton';
import { Text, TextTypography } from 'components/Text/Text';

import { ReactComponent as Logo } from 'assets/logo.svg';
import BlueSticker from 'assets/stickerExamples/blue-sticker_ru.png';
import GreenSticker from 'assets/stickerExamples/green-sticker_ru.png';
import BlueStickerEn from 'assets/stickerExamples/blue-sticker_en.png';
import GreenStickerEn from 'assets/stickerExamples/green-sticker_en.png';
import StepsSticker from 'assets/stickerExamples/steps-sticker.png';
import { Link } from 'react-router-dom';
import { StickerDownloadButton } from './components/StickerDownloadButton';
import i18n from '../../../i18n';
import { useSelector } from 'react-redux';
import { authIsoCodeSelector } from '../../state/auth/selectors';
import { SupportedCountryCodes } from '../../types';

const useStyles = makeStyles(theme => ({
    header: {
        textAlign: 'center',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    link: {
        color: theme.palette.fixedPrimary.primary,
    },
    material: {
        color: '#2697FF',
        marginBottom: 20,
    },
    titleBlock: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    listTitle: {
        marginBottom: theme.spacing(2),
    },
    stickerList: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: theme.spacing(-3),
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('lg')]: {
            flexWrap: 'wrap',
        },
    },
    stickerItem: {
        width: '30.6%',
        marginTop: theme.spacing(3),
        marginRight: '4%',
        '&:last-child': {
            marginRight: 0,
        },
        [theme.breakpoints.down(768)]: {
            width: '100%',
            marginRight: 0,
        },
    },
    stickerImage: {
        width: '100%',
    },
    openOriginal: {
        cursor: 'pointer',
        opacity: 0.6,
        textAlign: 'center',
    },
    downloadButton: {
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(2),
        },
    },
}));

interface MarketingMaterialsProps {
    onClose: () => void;
}

export const MarketingMaterials: FC<MarketingMaterialsProps> = ({ onClose }) => {
    const classes = useStyles();
    const {
        t,
        i18n: { language },
    } = i18n.useTranslation();
    const isoCode = useSelector(authIsoCodeSelector);
    const stickerExamples = [BlueSticker, GreenSticker, StepsSticker];
    const enStickerExamples = [BlueStickerEn, GreenStickerEn];
    return (
        <Box m={2} mb={4}>
            <div className={classes.header}>
                <Logo />
            </div>
            <BackButton onClick={onClose} />
            <div className={classes.titleBlock}>
                <Text typography={TextTypography.TITLE}>{t('vendor:marketingDocs:title')}</Text>
                <StickerDownloadButton className={classes.downloadButton} />
            </div>
            <Box>
                <Text typography={TextTypography.LEAD_STRONG} className={classes.listTitle}>
                    {t('vendor:marketingDocs:examples')}
                </Text>
                <div className={classes.stickerList}>
                    {(language === 'ru' ? stickerExamples : enStickerExamples).map((sticker, i) => (
                        <div key={i} className={classes.stickerItem}>
                            <img src={sticker} className={classes.stickerImage} />
                            <Link to={sticker} target="_blank" rel="noopener noreferrer">
                                <Text typography={TextTypography.BODY} className={classes.openOriginal}>
                                    {t('vendor:marketingDocs:openOriginal')}
                                </Text>
                            </Link>
                        </div>
                    ))}
                </div>
            </Box>
        </Box>
    );
};
